// @flow
import _ from 'lodash'
import {
  PAYMENT_CONTINGENCY_ACTIONS,
  SCAM_WARNING_TYPES,
  WARNING_INFORMATION_URLS,
} from '../../../../lib/constants'
import content from 'pp-react-l10n'

/**
 * Get a specific contingency from a list of them.
 */
export function getContingency(
  contingencies: Contingencies,
  action: ContingencyAction
): ContingencyDetail | void {
  if (!contingencies) {
    return
  }

  return contingencies.find(contingency => contingency?.action === action)
}

/**
 * Get a specific payment contingency from a list of them.
 */
export function getPaymentContingency(
  contingencies: PaymentContingencies,
  action: PaymentContingencyAction
): PaymentContingencyDetail | void {
  return contingencies.find(contingency => contingency?.action === action)
}

/**
 * Fetch Scam Warning contingency from a list of payment contingencies.
 */
export function getScamContingency(
  contingencies: PaymentContingencies
): PaymentContingencyDetail | void {
  if (!contingencies) {
    return
  }

  return getPaymentContingency(
    contingencies,
    PAYMENT_CONTINGENCY_ACTIONS.DISPLAY_SCAM_WARNING
  )
}

/**
 * Fetch Scam Warning contingency action from a a list of payment contingencies.
 */
export function getScamContingencyAction(
  contingencies: PaymentContingencies
): PaymentContingencyDetail | void {
  const scamContignency = getScamContingency(contingencies)

  return scamContignency?.action
}

/**
 * Get the Scam Warning from contingency
 */
export function getScamWarning(
  contingency: PaymentContingencyDetail | void
): ScamWarningDetails | void {
  return contingency?.scamWarning
}

/**
 * Get the Warning type from a Scam Warning Contingency
 */
export function getWarningType(
  scamWarning: ScamWarningDetails | void
): ScamWarningType | void {
  return scamWarning?.type
}

/**
 * Get the Warning type from a list of contingencies
 */
export function getScamWarningType(
  contingencies: PaymentContingencies
): ScamWarningType | void {
  const scamContingency = getScamContingency(contingencies)
  const scamWarning = getScamWarning(scamContingency)
  return getWarningType(scamWarning)
}

/**
 * Get if Scam Warning contingency is active
 */
export function isScamWarningUnresolved(
  contingencies: PaymentContingencies
): boolean {
  const scamContingency = getScamContingency(contingencies)
  return !!scamContingency
}

/**
 * Get the titles used by the warning overpanel for a risk level. It's a separate utility because it's used both by the warning
 * and the container overpanel.
 */
export function getScamWarningTitle(warningType: ScamWarningType): string {
  const scamI18n = content('transfer/scam')

  const scamWarningTitleMap = {
    [SCAM_WARNING_TYPES.LOW_RISK]: getI18nContent(
      scamI18n,
      'scam.header.lowRisk'
    ),
    [SCAM_WARNING_TYPES.MEDIUM_RISK]: getI18nContent(
      scamI18n,
      'scam.header.mediumRisk'
    ),
    [SCAM_WARNING_TYPES.HIGH_CONFIDENCE_COMMERCE]: getI18nContent(
      scamI18n,
      'scam.header.highConfidenceCommerce'
    ),
  }

  return scamWarningTitleMap[warningType] || ''
}

/**
 * Get i18n text given a content function and a key.
 */
export function getI18nContent(
  i18nContent: Function,
  propertyKey: string
): string | void {
  if (typeof i18nContent === 'function') {
    return i18nContent(propertyKey)
  }
}

/**
 * Check if the warning type is a scam warning. The other option is it being a "High Confidence Commerce" warning
 */
export function isWarningScam(warningType: ScamWarningType): boolean {
  return (
    warningType === SCAM_WARNING_TYPES.LOW_RISK ||
    warningType === SCAM_WARNING_TYPES.MEDIUM_RISK
  )
}

/**
 * Check if the warning type is High Confidence Commerce.
 */
export function isWarningHighConfidence(warningType: ScamWarningType): boolean {
  return warningType === SCAM_WARNING_TYPES.HIGH_CONFIDENCE_COMMERCE
}

/**
 * Get localized information url. Empty url if country is undefined.
 */
export function getLocalInfoUrl(country: string) {
  if (country) {
    return WARNING_INFORMATION_URLS.LEARN_ABOUT_SCAMS.replace(
      ':country',
      country
    )
  }

  return '#'
}

/**
 * Get the funding option Id required for contignency resolution: the preselected one if possible, if not the first one.
 */
export function getFundingIdForContingencyResolutions(
  fundingOptions: Array<Object>
) {
  if (!fundingOptions || fundingOptions.length === 0) {
    return ''
  }

  const preselectedFundingOption = fundingOptions.find(
    option => option?.preselected_funding_option
  )
  const preselectedFundingOptionId = preselectedFundingOption?.id

  const firstFundingOptionId = fundingOptions[0].id

  return preselectedFundingOptionId ?? firstFundingOptionId
}

export function getPurposeCodeCollectionContingency(contingencies: any) {
  return getContingency(
    contingencies,
    PAYMENT_CONTINGENCY_ACTIONS.PURPOSE_CODE_COLLECTION_REQUIRED
  )
}

export function hasContingencyActionAttribute(
  contingencies: any,
  contingencyAction: PaymentContingencyAction | ContingencyAction,
  contingencyActionAttribute: UserDataCollectionAttribute
) {
  const contingency = getContingency(contingencies, contingencyAction)
  const attributes = _.get(contingency, 'data_collection_details.attributes')
  return attributes && attributes.includes(contingencyActionAttribute)
}
